/**
 * PHASE 10 — calibration overlay.
 *
 * Guides the manual compass calibration: aim the top of the phone at a known
 * landmark (true north), read the live heading, then declare it with
 * "Set North". The parent wires the resulting offset into the CalibrationStore.
 */

export interface CalibrationOverlayOptions {
  onSetNorth: (headingDeg: number) => void;
  onCancel: () => void;
}

export class CalibrationOverlay {
  private headingEl: HTMLElement;
  private setNorthBtn: HTMLButtonElement;

  constructor(
    private root: HTMLElement,
    options: CalibrationOverlayOptions,
  ) {
    this.headingEl = root.querySelector<HTMLElement>(".calib-heading") ?? document.createElement("div");
    this.setNorthBtn = root.querySelector<HTMLButtonElement>(".calib-set-north") ?? document.createElement("button");
    root.querySelector<HTMLButtonElement>(".calib-cancel")?.addEventListener("click", () => options.onCancel());
    this.setNorthBtn.addEventListener("click", () => {
      const current = Number(this.headingEl.dataset.deg ?? "NaN");
      if (Number.isFinite(current) && this.headingEl.dataset.usable === "yes") {
        options.onSetNorth(current);
      }
    });
  }

  get isOpen(): boolean {
    return this.root.style.display !== "none";
  }

  show(): void {
    this.root.style.display = "block";
    this.setNorthBtn.disabled = true;
  }

  hide(): void {
    this.root.style.display = "none";
  }

  /** Live heading readout; null hides the readout (no orientation yet). */
  sync(headingDeg: number | null, usable: boolean): void {
    if (headingDeg === null) {
      this.headingEl.textContent = "waiting for orientation…";
      this.headingEl.dataset.usable = "no";
    } else {
      this.headingEl.textContent = `${Math.round(headingDeg).toString().padStart(3, "0")}°`;
      this.headingEl.dataset.deg = String(headingDeg);
      this.headingEl.dataset.usable = usable ? "yes" : "no";
    }
    this.setNorthBtn.disabled = headingDeg === null || !usable;
  }
}